/* ============================================================
   ProvidersPage — TASK-247: public provider catalog / trust
   surface. Browse available 3D-print performers with rating,
   verified badge, completed orders and technologies; open a
   provider detail with recent published reviews.
   TASK-249: city/technology/verified filters + pagination with
   server-side totalCount.
   PF-5 (TASK-e15bdfab): «влезет X×Y×Z» min-build-volume filter
   — providers whose ANY active printer fits; browse fetch moved
   to the standalone providerCatalogApi (client.ts untouched,
   concurrent PF-3 in-flight work).
   ============================================================ */

import { useCallback, useEffect, useState } from 'react';
import { api, resolveMediaUrl } from '../api/client';
import { browseProviderCatalog } from '../api/providerCatalogApi';
import { useAuth } from '../auth/AuthContext';
import { providerLogoView, summaryCoverUrl } from './mediaGalleryView';
import { FileUpload } from './FileUpload';
import { QuoteDisplay } from './QuoteDisplay';
import { ListPager } from './ListPager';
import { buildSellerQuoteFormView, clampCopies } from './sellerQuoteView';
import { formatRub } from '../types/product';
import type { ProductSummary } from '../types/product';
import { QUALITY_PRESETS, type Material, type ModelFileResponse, type QualityPreset, type QuoteResponse } from '../types';
import {
  buildVolumeFilterLabel,
  parseBuildVolumeFilter,
} from './providerBuildVolumeFilterView';
import { heartLabel } from './favoritesView';
import type {
  ProviderCatalogBrowseResult,
  ProviderCatalogDetail,
  ProviderCatalogItem,
} from '../types/provider';

/* Numeric guards — API may deliver numeric enums / nulls. */
function num(value: number | null | undefined): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function ratingLabel(rating: number | null, reviewCount: number): string {
  if (rating === null || rating === undefined) return 'Нет отзывов';
  return `★ ${num(rating).toFixed(1)} · ${num(reviewCount)} отзыв(ов)`;
}

function responseLabel(hours: number | null): string | null {
  if (hours === null || hours === undefined) return null;
  const h = num(hours);
  if (h <= 0) return null;
  return h < 24
    ? `Отвечает ~${Math.max(1, Math.round(h))} ч`
    : `Отвечает ~${Math.max(1, Math.round(h / 24))} дн`;
}

const trustBadgeStyle = {
  fontSize: 'var(--fs-caption)',
  padding: '2px 8px',
  borderRadius: 'var(--radius-sm)',
  background: 'rgba(255,149,0,0.08)',
  color: 'var(--color-primary)',
  fontWeight: 600,
} as const;

function TrustBadges({ item }: { item: ProviderCatalogItem }) {
  return (
    <div style={{ display: 'flex', gap: 'var(--space-xs)', flexWrap: 'wrap', alignItems: 'center' }}>
      {Boolean(item.isVerified) && (
        <span style={trustBadgeStyle}>✓ Проверен</span>
      )}
      {num(item.completedOrderCount) > 0 && (
        <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
          {num(item.completedOrderCount)} заказ(ов) выполнено
        </span>
      )}
    </div>
  );
}

function ProviderRow({
  item,
  expanded,
  onToggle,
}: {
  item: ProviderCatalogItem;
  expanded: boolean;
  onToggle: () => void;
}) {
  const response = responseLabel(item.averageResponseHours);
  return (
    <div className="card" style={{ padding: 'var(--space-lg)', textAlign: 'left' }}>
      <button
        onClick={onToggle}
        style={{ all: 'unset', cursor: 'pointer', display: 'block', width: '100%' }}
        aria-expanded={expanded}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-md)', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', minWidth: 0 }}>
            {providerLogoView(item.logoUrl) ? (
              <img
                src={resolveMediaUrl(providerLogoView(item.logoUrl)!)}
                alt=""
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 'var(--radius-md)',
                  objectFit: 'cover',
                  border: '1px solid var(--color-separator)',
                  flexShrink: 0,
                }}
              />
            ) : null}
            <div>
              <span style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-semibold)' }}>
                {item.companyName}
              </span>
              {item.city ? (
                <span style={{ color: 'var(--color-text-secondary)', marginLeft: 'var(--space-sm)' }}>{item.city}</span>
              ) : null}
            </div>
          </div>
          <span style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--fs-body)' }}>
            {ratingLabel(item.rating, item.reviewCount)}
          </span>
        </div>
        {item.bio ? (
          <p style={{ color: 'var(--color-text-secondary)', marginTop: 'var(--space-xs)', fontSize: 'var(--fs-body)' }}>
            {item.bio}
          </p>
        ) : null}
        <div style={{ display: 'flex', gap: 'var(--space-sm)', flexWrap: 'wrap', marginTop: 'var(--space-sm)', alignItems: 'center' }}>
          <TrustBadges item={item} />
          {response ? (
            <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>{response}</span>
          ) : null}
          {(item.technologies ?? []).length > 0 && (
            <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
              · {(item.technologies ?? []).join(' / ')}
            </span>
          )}
        </div>
      </button>
    </div>
  );
}

/** 054819ad: directed quote — upload a model, price it with THIS seller's
 *  rates (POST /quotes { providerId } → TASK-179 seller pricing path). */
function SellerQuoteCard({ providerId, providerName }: { providerId: string; providerName: string }) {
  const [modelFile, setModelFile] = useState<ModelFileResponse | null>(null);
  const [materials, setMaterials] = useState<Material[]>([]);
  const [materialId, setMaterialId] = useState<string | null>(null);
  const [quality, setQuality] = useState<QualityPreset>(QUALITY_PRESETS[1]); // standard
  const [copies, setCopies] = useState(1);
  const [quote, setQuote] = useState<QuoteResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.getMaterials()
      .then((mats) => {
        if (cancelled) return;
        setMaterials(mats);
        if (mats.length > 0) setMaterialId(mats[0].id);
      })
      .catch(() => { /* quote card degrades: no materials → hint stays */ });
    return () => { cancelled = true; };
  }, []);

  const formView = buildSellerQuoteFormView({
    modelFileId: modelFile?.id ?? null,
    materialId,
    copies,
    busy,
  });

  const requestQuote = async () => {
    if (!modelFile || !materialId) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.createQuote({
        modelFileId: modelFile.id,
        materialId,
        layerHeightMm: quality.layerHeightMm,
        infillPercent: quality.infillPercent,
        copies: formView.copies,
        providerId,
      });
      setQuote(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось рассчитать');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card" style={{ padding: 'var(--space-lg)' }}>
      <h3 style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-semibold)', marginBottom: 'var(--space-xs)' }}>
        Печать своей модели у «{providerName}»
      </h3>
      <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--fs-body)', marginBottom: 'var(--space-md)' }}>
        Загрузите модель — продавец рассчитает стоимость по своим тарифам
      </p>
      {!modelFile ? (
        <FileUpload onUploaded={(f) => { setModelFile(f); setQuote(null); }} />
      ) : (
        <div style={{ display: 'grid', gap: 'var(--space-md)' }}>
          <div style={{ display: 'flex', gap: 'var(--space-md)', flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 'var(--fs-body)', fontWeight: 600 }}>{modelFile.originalFileName}</span>
            <button className="btn btn-tertiary" style={{ height: 32, padding: '4px 12px' }} onClick={() => { setModelFile(null); setQuote(null); }}>
              Заменить
            </button>
          </div>
          <div style={{ display: 'grid', gap: 'var(--space-sm)', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
            <label style={{ display: 'grid', gap: 'var(--space-xxs)', fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
              Материал
              <select value={materialId ?? ''} onChange={(e) => setMaterialId(e.target.value)} style={filterInputStyle}>
                {materials.length === 0 && <option value="">Нет материалов</option>}
                {materials.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </label>
            <label style={{ display: 'grid', gap: 'var(--space-xxs)', fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
              Качество
              <select value={quality.key} onChange={(e) => setQuality(QUALITY_PRESETS.find((p) => p.key === e.target.value) ?? QUALITY_PRESETS[1])} style={filterInputStyle}>
                {QUALITY_PRESETS.map((p) => <option key={p.key} value={p.key}>{p.label} · {p.layerHeightMm.toFixed(2)} мм</option>)}
              </select>
            </label>
            <label style={{ display: 'grid', gap: 'var(--space-xxs)', fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
              Экземпляров
              <input
                type="number"
                min={1}
                max={99}
                value={formView.copies}
                onChange={(e) => { setCopies(clampCopies(Number(e.target.value))); setQuote(null); }}
                style={filterInputStyle}
              />
            </label>
          </div>
          <div style={{ display: 'flex', gap: 'var(--space-md)', alignItems: 'center', flexWrap: 'wrap' }}>
            <button className="btn" onClick={() => void requestQuote()} disabled={!formView.canQuote}>
              {formView.buttonLabel}
            </button>
            {formView.hint && <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>{formView.hint}</span>}
          </div>
          {error && <p style={{ color: 'var(--color-danger)', fontSize: 'var(--fs-body)' }}>{error}</p>}
          {quote && modelFile && <QuoteDisplay quote={quote} modelFile={modelFile} />}
        </div>
      )}
    </div>
  );
}

/** 054819ad: seller's published products — server-side pagination via
 *  GET /products/page?providerId (the DeliveryPoints paged DTO pattern). */
function SellerProductsCard({ providerId, onOpenProduct }: { providerId: string; onOpenProduct: (id: string) => void }) {
  const SELLER_PAGE_SIZE = 12;
  const [products, setProducts] = useState<ProductSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.getProductPage({ providerId, offset, limit: SELLER_PAGE_SIZE })
      .then((page) => {
        if (cancelled) return;
        setProducts(page.items);
        setTotal(page.total);
      })
      .catch((err) => { if (!cancelled) setError(err instanceof Error ? err.message : 'Не удалось загрузить товары'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [providerId, offset]);

  return (
    <div className="card" style={{ padding: 'var(--space-lg)' }}>
      <h3 style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-semibold)', marginBottom: 'var(--space-md)' }}>
        Товары продавца
      </h3>
      {loading ? (
        <div className="skeleton" style={{ height: 160, borderRadius: 'var(--radius-lg)' }} />
      ) : error ? (
        <p style={{ color: 'var(--color-danger)' }}>{error}</p>
      ) : products.length === 0 ? (
        <p style={{ color: 'var(--color-text-secondary)' }}>Продавец пока не опубликовал товары</p>
      ) : (
        <>
          <div style={{ display: 'grid', gap: 'var(--space-md)', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))' }}>
            {products.map((p) => {
              const cover = summaryCoverUrl(p);
              return (
                <button
                  key={p.productId}
                  onClick={() => onOpenProduct(p.productId)}
                  className="card"
                  style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xs)', textAlign: 'left', cursor: 'pointer', padding: 'var(--space-md)' }}
                >
                  {cover ? (
                    <img src={resolveMediaUrl(cover)} alt={p.title} loading="lazy" style={{ height: 110, borderRadius: 'var(--radius-md)', objectFit: 'cover', width: '100%' }} />
                  ) : (
                    <div style={{ height: 110, borderRadius: 'var(--radius-md)', background: 'linear-gradient(135deg, var(--color-primary), var(--color-secondary))', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 'var(--fs-h1)', fontWeight: 'var(--fw-bold)' }}>
                      {p.title.slice(0, 1).toUpperCase()}
                    </div>
                  )}
                  <span style={{ fontWeight: 'var(--fw-semibold)', fontSize: 'var(--fs-body)' }}>{p.title}</span>
                  <span style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-bold)', color: 'var(--color-primary)' }}>{formatRub(p.priceRub)}</span>
                </button>
              );
            })}
          </div>
          <ListPager
            total={total}
            offset={offset}
            limit={SELLER_PAGE_SIZE}
            onPrev={() => setOffset(Math.max(0, offset - SELLER_PAGE_SIZE))}
            onNext={() => setOffset(offset + SELLER_PAGE_SIZE)}
          />
        </>
      )}
    </div>
  );
}

function ProviderDetail({ providerUserId, onBack, onOpenProduct }: { providerUserId: string; onBack: () => void; onOpenProduct: (id: string) => void }) {
  const { isAuthenticated } = useAuth();
  const [detail, setDetail] = useState<ProviderCatalogDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  // CB-6 (a90f4a83): heart state for this performer (authenticated users).
  const [favorited, setFavorited] = useState(false);
  const [favoriteBusy, setFavoriteBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setDetail(null);
    setError(null);
    api.getProviderCatalogDetail(providerUserId)
      .then((d) => { if (!cancelled) setDetail(d); })
      .catch((err) => { if (!cancelled) setError(err instanceof Error ? err.message : 'Не удалось загрузить исполнителя'); });
    return () => { cancelled = true; };
  }, [providerUserId]);

  useEffect(() => {
    // CB-6: heart state rides the compact id fetch — one call per view.
    if (!isAuthenticated) {
      setFavorited(false);
      return;
    }
    api.getFavoriteIds()
      .then((ids) => setFavorited((ids.providerUserIds ?? []).includes(providerUserId)))
      .catch(() => {});
  }, [providerUserId, isAuthenticated]);

  const toggleFavorite = async () => {
    setFavoriteBusy(true);
    setError(null);
    try {
      const result = await api.toggleProviderFavorite(providerUserId);
      setFavorited(result.favorited);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось обновить избранное');
    } finally {
      setFavoriteBusy(false);
    }
  };

  if (error) {
    return (
      <div className="card" style={{ textAlign: 'center' }}>
        <p style={{ color: 'var(--color-danger)' }}>{error}</p>
        <button className="btn btn-secondary" onClick={onBack} style={{ marginTop: 'var(--space-md)' }}>Назад к списку</button>
      </div>
    );
  }

  if (!detail) return <div className="skeleton" style={{ height: 260, borderRadius: 'var(--radius-lg)' }} />;

  const response = responseLabel(detail.averageResponseHours);
  return (
    <div style={{ display: 'grid', gap: 'var(--space-md)' }}>
      <button className="btn btn-secondary" onClick={onBack} style={{ justifySelf: 'start' }}>
        ← Все исполнители
      </button>

      <div className="card" style={{ padding: 'var(--space-lg)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-md)', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
            {providerLogoView(detail.logoUrl) ? (
              <img
                src={resolveMediaUrl(providerLogoView(detail.logoUrl)!)}
                alt=""
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: 'var(--radius-md)',
                  objectFit: 'cover',
                  border: '1px solid var(--color-separator)',
                }}
              />
            ) : null}
            <div>
              <h2 style={{ fontSize: 'var(--fs-h2)', fontWeight: 'var(--fw-bold)' }}>{detail.companyName}</h2>
              <p style={{ color: 'var(--color-text-secondary)', marginTop: 'var(--space-xs)' }}>
                {detail.city || 'Город не указан'}
                {detail.websiteUrl ? ` · ${detail.websiteUrl}` : ''}
              </p>
            </div>
          </div>
          <span style={{ fontSize: 'var(--fs-h3)', color: 'var(--color-primary)', fontWeight: 600 }}>
            {ratingLabel(detail.rating, detail.reviewCount)}
          </span>
        </div>
        <button
          className="btn btn-secondary"
          onClick={() => void toggleFavorite()}
          disabled={favoriteBusy}
          style={{ marginTop: 'var(--space-md)', justifySelf: 'start', color: favorited ? 'var(--color-primary)' : undefined }}
        >
          {favoriteBusy ? '…' : heartLabel(favorited)}
        </button>
        {detail.bio ? <p style={{ marginTop: 'var(--space-md)' }}>{detail.bio}</p> : null}
        <div style={{ display: 'flex', gap: 'var(--space-md)', flexWrap: 'wrap', marginTop: 'var(--space-md)' }}>
          <TrustBadges item={detail} />
          {response ? <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>{response}</span> : null}
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-lg)', flexWrap: 'wrap', marginTop: 'var(--space-md)', color: 'var(--color-text-secondary)', fontSize: 'var(--fs-body)' }}>
          <span>Активных принтеров: {num(detail.activePrinterCount)}</span>
          {(detail.technologies ?? []).length > 0 && <span>Технологии: {(detail.technologies ?? []).join(' / ')}</span>}
          <span>Товаров в магазине: {num(detail.shopProductCount)}</span>
        </div>
      </div>

      <SellerQuoteCard providerId={providerUserId} providerName={detail.companyName} />

      <SellerProductsCard providerId={providerUserId} onOpenProduct={onOpenProduct} />

      <div className="card" style={{ padding: 'var(--space-lg)' }}>
        <h3 style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-semibold)', marginBottom: 'var(--space-md)' }}>
          Отзывы
        </h3>
        {detail.recentReviews?.length ? (
          <div style={{ display: 'grid', gap: 'var(--space-md)' }}>
            {detail.recentReviews.map((r) => (
              <div key={r.reviewId} style={{ borderTop: '1px solid var(--color-separator)', paddingTop: 'var(--space-md)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600 }}>{r.authorName}</span>
                  <span style={{ color: 'var(--color-primary)' }}>{'★'.repeat(Math.max(0, Math.min(5, num(r.rating))))}</span>
                </div>
                <p style={{ color: 'var(--color-text-secondary)', marginTop: 'var(--space-xs)', whiteSpace: 'pre-wrap' }}>
                  {r.comment}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <p style={{ color: 'var(--color-text-secondary)' }}>Пока нет опубликованных отзывов</p>
        )}
      </div>
    </div>
  );
}

/** Filter-bar input style (matches the search field, light theme). */
const filterInputStyle = {
  height: 44,
  padding: '0 var(--space-md)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--color-separator)',
  background: 'var(--color-surface)',
  fontSize: 'var(--fs-body)',
  color: 'var(--color-text)',
} as const;

const TECHNOLOGIES = ['FDM', 'SLA', 'SLS'] as const;
const PAGE_SIZE = 12;

export function ProvidersPage({ onOpenProduct }: { onOpenProduct: (id: string) => void }) {
  const [result, setResult] = useState<ProviderCatalogBrowseResult | null>(null);
  const [query, setQuery] = useState('');
  const [city, setCity] = useState('');
  const [technology, setTechnology] = useState('');
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [cityOptions, setCityOptions] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // PF-5 «влезет X×Y×Z»: raw axis inputs; parsed into the browse filter.
  const [buildX, setBuildX] = useState('');
  const [buildY, setBuildY] = useState('');
  const [buildZ, setBuildZ] = useState('');
  const volumeParse = parseBuildVolumeFilter(buildX, buildY, buildZ);
  const volumeChip = buildVolumeFilterLabel(volumeParse.filter);

  const load = useCallback(async () => {
    setError(null);
    try {
      const data = await browseProviderCatalog({
        q: query.trim() || undefined,
        city: city || undefined,
        technology: technology || undefined,
        verified: verifiedOnly || undefined,
        ...(volumeParse.error ? {} : volumeParse.filter),
        offset: page * PAGE_SIZE,
        limit: PAGE_SIZE,
      });
      setResult(data);
      // Accumulate distinct city options from server data (no extra endpoint).
      setCityOptions((prev) => {
        const seen = new Set(prev);
        data.items.forEach((i) => { if (i.city) seen.add(i.city); });
        const next = [...seen].sort((a, b) => a.localeCompare(b, 'ru'));
        return next.length === prev.length && next.every((c, i) => c === prev[i]) ? prev : next;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка загрузки');
    }
  }, [query, city, technology, verifiedOnly, page, buildX, buildY, buildZ, volumeParse.error, volumeParse.filter]);

  useEffect(() => {
    const t = setTimeout(() => { void load(); }, 250);
    return () => clearTimeout(t);
  }, [load]);

  const providers = result?.items ?? null;
  const totalCount = result?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const resetPage = <T,>(setter: (v: T) => void) => (v: T) => {
    setter(v);
    setPage(0);
  };

  if (selectedId) {
    return (
      <div className="container" style={{ paddingTop: 'var(--space-xl)', paddingBottom: 'var(--space-2xl)' }}>
        <ProviderDetail providerUserId={selectedId} onBack={() => setSelectedId(null)} onOpenProduct={onOpenProduct} />
      </div>
    );
  }

  return (
    <div className="container" style={{ paddingTop: 'var(--space-xl)', paddingBottom: 'var(--space-2xl)' }}>
      <div style={{ marginBottom: 'var(--space-lg)' }}>
        <h1 style={{ fontSize: 'var(--fs-h1)', fontWeight: 'var(--fw-bold)', letterSpacing: '-0.02em' }}>
          Исполнители
        </h1>
        <p style={{ fontSize: 'var(--fs-body)', color: 'var(--color-text-secondary)', marginTop: 'var(--space-xs)' }}>
          Проверенные 3D-печатью мастерские: рейтинг, отзывы и количество выполненных заказов
        </p>
      </div>

      <input
        type="search"
        placeholder="Поиск по названию, описанию или городу…"
        value={query}
        onChange={(e) => resetPage(setQuery)(e.target.value)}
        style={{ ...filterInputStyle, width: '100%', marginBottom: 'var(--space-md)' }}
      />

      <div style={{ display: 'flex', gap: 'var(--space-sm)', flexWrap: 'wrap', alignItems: 'center', marginBottom: 'var(--space-lg)' }}>
        <select
          aria-label="Город"
          value={city}
          onChange={(e) => resetPage(setCity)(e.target.value)}
          style={{ ...filterInputStyle, minWidth: 160 }}
        >
          <option value="">Все города</option>
          {cityOptions.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select
          aria-label="Технология"
          value={technology}
          onChange={(e) => resetPage(setTechnology)(e.target.value)}
          style={{ ...filterInputStyle, minWidth: 140 }}
        >
          <option value="">Все технологии</option>
          {TECHNOLOGIES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <button
          type="button"
          onClick={() => resetPage(setVerifiedOnly)(!verifiedOnly)}
          aria-pressed={verifiedOnly}
          style={{
            ...filterInputStyle,
            cursor: 'pointer',
            fontWeight: verifiedOnly ? 600 : 400,
            borderColor: verifiedOnly ? 'var(--color-primary)' : 'var(--color-separator)',
            background: verifiedOnly ? 'rgba(255,149,0,0.10)' : 'var(--color-surface)',
            color: verifiedOnly ? 'var(--color-primary)' : 'var(--color-text)',
          }}
        >
          ✓ Только проверенные
        </button>
        {/* PF-5: «влезет X×Y×Z» min-build-volume filter (mm, inclusive). */}
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: 'var(--space-xs)',
            padding: '0 var(--space-sm)', height: 44,
            borderRadius: 'var(--radius-md)',
            border: `1px solid ${volumeChip && !volumeParse.error ? 'var(--color-primary)' : 'var(--color-separator)'}`,
            background: volumeChip && !volumeParse.error ? 'rgba(255,149,0,0.10)' : 'var(--color-surface)',
          }}
          aria-label="Модель влезет в принтер, мм"
        >
          <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>
            Влезет, мм
          </span>
          {([['X', buildX, setBuildX], ['Y', buildY, setBuildY], ['Z', buildZ, setBuildZ]] as const).map(
            ([axis, value, setter]) => (
              <input
                key={axis}
                type="number"
                min={1}
                step="any"
                inputMode="decimal"
                aria-label={`Размер ${axis}, мм`}
                placeholder={axis}
                value={value}
                onChange={(e) => resetPage(setter)(e.target.value)}
                style={{
                  width: 64, height: 32, textAlign: 'center',
                  padding: '0 var(--space-xs)',
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--color-separator)',
                  background: 'var(--color-surface)',
                  fontSize: 'var(--fs-body)', color: 'var(--color-text)',
                }}
              />
            ),
          )}
        </div>
        {volumeParse.error ? (
          <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-danger)' }}>
            {volumeParse.error}
          </span>
        ) : null}
      </div>

      {error ? (
        <div className="card" style={{ textAlign: 'center' }}>
          <p style={{ color: 'var(--color-danger)' }}>{error}</p>
          <button className="btn btn-secondary" onClick={() => void load()} style={{ marginTop: 'var(--space-md)' }}>
            Повторить
          </button>
        </div>
      ) : providers === null ? (
        <div style={{ display: 'grid', gap: 'var(--space-md)' }}>
          {[1, 2, 3].map((i) => <div key={i} className="skeleton" style={{ height: 130, borderRadius: 'var(--radius-lg)' }} />)}
        </div>
      ) : providers.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 'var(--space-2xl)' }}>
          <p style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-semibold)' }}>Ничего не найдено</p>
          <p style={{ color: 'var(--color-text-secondary)', marginTop: 'var(--space-xs)' }}>
            Попробуйте изменить запрос или фильтры — или закажите печать своей модели на главной
          </p>
        </div>
      ) : (
        <>
          <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)', marginBottom: 'var(--space-md)' }}>
            Найдено: {totalCount}
          </p>
          <div style={{ display: 'grid', gap: 'var(--space-md)' }}>
            {providers.map((p) => (
              <ProviderRow
                key={p.providerUserId}
                item={p}
                expanded={false}
                onToggle={() => setSelectedId(p.providerUserId)}
              />
            ))}
          </div>
          {totalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-md)', marginTop: 'var(--space-xl)', flexWrap: 'wrap' }}>
              <button
                className="btn btn-secondary"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                ← Назад
              </button>
              <span style={{ fontSize: 'var(--fs-body)', color: 'var(--color-text-secondary)' }}>
                Страница {page + 1} из {totalPages}
              </span>
              <button
                className="btn btn-secondary"
                disabled={page >= totalPages - 1}
                onClick={() => setPage((p) => p + 1)}
              >
                Вперёд →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
